import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { featuredPost, pillarCategories } from '../../mocks/blog-posts';
import { featuredProducts } from '../../mocks/products';
import { useState } from 'react';
import { useSEO } from '../../utils/seo';

export default function Home() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  // SEO Configuration
  useSEO({
    title: 'Rooted & Rising - Build a Life That Matters, One Small Habit at a Time',
    description: 'Transform your family through micro-habits rooted in Faith, Family, Friends, Fitness, Finances, Food, and Fun. Build small daily actions that compound over a lifetime with Rooted & Rising.',
    keywords: 'family habits, micro-habits, faith-based family, family fitness, family finances, intentional living, habit stacking, family growth, atomic habits',
    canonical: '/',
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': `${import.meta.env.VITE_SITE_URL || ''}/#organization`,
          name: 'Rooted & Rising',
          url: import.meta.env.VITE_SITE_URL || '',
          description: 'A family operating system for intentional living through micro-habits',
          sameAs: []
        },
        {
          '@type': 'WebSite',
          '@id': `${import.meta.env.VITE_SITE_URL || ''}/#website`,
          url: import.meta.env.VITE_SITE_URL || '',
          name: 'Rooted & Rising',
          description: 'Transform your family through micro-habits rooted in Faith, Family, Friends, Fitness, Finances, Food, and Fun',
          publisher: {
            '@id': `${import.meta.env.VITE_SITE_URL || ''}/#organization`
          }
        }
      ]
    }
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d5qnkuahs78ip5370aag', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 3000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://public.readdy.ai/ai/img_res/eb4436028be58f2fd8d0c57dd9bccb6f.jpg')"
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center w-full">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Build a Life That Matters,<br />One Small Habit at a Time
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl mx-auto">
            Transform your family through micro-habits rooted in Faith, Family, Friends, Fitness, Finances, Food, and Fun
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/how-it-works" 
              className="px-8 py-4 bg-amber-500 text-white rounded-full font-medium hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Discover How It Works
            </Link>
            <Link 
              to="/blog" 
              className="px-8 py-4 bg-white text-gray-900 rounded-full font-medium hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Explore the Blog
            </Link>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Small Actions, Lasting Transformation
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Rooted &amp; Rising is your family operating system for intentional living. We help families build micro-habits that compound over a lifetime—without the overwhelm.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-amber-500 rounded-full mx-auto mb-6">
                <i className="ri-seedling-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Start Small</h3>
              <p className="text-gray-600 leading-relaxed">
                Micro-habits take just 2-10 minutes but create compound growth over time. No overwhelm, just progress.
              </p>
            </div>

            <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-rose-500 rounded-full mx-auto mb-6">
                <i className="ri-stack-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Stack Habits</h3>
              <p className="text-gray-600 leading-relaxed">
                Link new habits to existing routines. Build momentum across all seven pillars of family life.
              </p>
            </div>

            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-emerald-500 rounded-full mx-auto mb-6">
                <i className="ri-team-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Grow Together</h3>
              <p className="text-gray-600 leading-relaxed">
                Shared habits create connection. Build rhythms that strengthen bonds across generations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Seven Pillars */}
      <section className="py-24 px-4 bg-gradient-to-br from-amber-50 via-rose-50 to-orange-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              The Seven Pillars
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A holistic framework for family growth. Each pillar supports the others, creating a balanced life rooted in what matters most.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {pillarCategories.map((pillar) => (
              <Link
                key={pillar.name}
                to="/pillars"
                className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              >
                <div className={`w-12 h-12 flex items-center justify-center ${pillar.color} rounded-full mx-auto mb-4`}>
                  <i className={`ri-${getPillarIcon(pillar.name)}-line text-xl`}></i>
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">{pillar.name}</h3>
                <p className="text-xs text-gray-500">{pillar.count} articles</p>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/pillars" 
              className="inline-flex items-center px-6 py-3 bg-amber-500 text-white rounded-full font-medium hover:bg-amber-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Explore All Pillars
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Blog Post */}
      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Latest from the Blog
              </h2>
              <p className="text-lg text-gray-600">
                Practical guidance for building micro-habits that transform family life
              </p>
            </div>
            <Link 
              to="/blog" 
              className="hidden md:inline-flex items-center text-amber-600 font-medium hover:text-amber-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              View All Posts
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <div className="relative h-96 lg:h-auto rounded-2xl overflow-hidden">
              <img 
                src={featuredPost.image} 
                alt={featuredPost.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex flex-col justify-center">
              <span className="inline-block px-4 py-1 bg-rose-100 text-rose-800 rounded-full text-sm font-medium mb-4 w-fit">
                {featuredPost.pillar}
              </span>
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {featuredPost.title}
              </h3>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {featuredPost.excerpt}
              </p>
              <div className="flex items-center text-sm text-gray-500 mb-6">
                <span>{featuredPost.date}</span>
                <span className="mx-3">•</span>
                <span>{featuredPost.readTime}</span>
              </div>
              <Link 
                to="/blog" 
                className="inline-flex items-center px-6 py-3 bg-gray-900 text-white rounded-full font-medium hover:bg-gray-800 transition-colors w-fit whitespace-nowrap cursor-pointer"
              >
                Read Article
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>

          <Link 
            to="/blog" 
            className="md:hidden flex items-center justify-center text-amber-600 font-medium hover:text-amber-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            View All Posts
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 px-4 bg-gradient-to-br from-rose-50 to-amber-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Tools for Your Journey
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Carefully curated products to support your family's micro-habit journey
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
            {featuredProducts.map((product) => (
              <div key={product.id} className="bg-white rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer">
                <div className="relative h-64 w-full">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <span className="text-xs text-gray-500 uppercase tracking-wide">{product.category}</span>
                  <h3 className="text-lg font-semibold text-gray-900 mt-2 mb-3">{product.name}</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-bold text-amber-600">${product.price}</span>
                    <button className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors whitespace-nowrap">
                      Add to Cart
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/store" 
              className="inline-flex items-center px-6 py-3 bg-gray-900 text-white rounded-full font-medium hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Visit Store
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-amber-500 to-rose-500 rounded-3xl p-12 md:p-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Join the Rooted &amp; Rising Community
            </h2>
            <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Get weekly micro-habit ideas, family rhythms, and practical guidance delivered to your inbox
            </p>
            <form 
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto" 
              data-readdy-form 
              id="newsletter-signup"
            >
              <input 
                type="email" 
                name="email"
                placeholder="Enter your email" 
                className="flex-1 px-6 py-4 rounded-full text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
                required
                disabled={formStatus === 'submitting'}
              />
              <button 
                type="submit"
                disabled={formStatus === 'submitting'}
                className="px-8 py-4 bg-gray-900 text-white rounded-full font-medium hover:bg-gray-800 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting' ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
            {formStatus === 'success' && (
              <p className="mt-4 text-white font-medium">Thank you for subscribing!</p>
            )}
            {formStatus === 'error' && (
              <p className="mt-4 text-white font-medium">Something went wrong. Please try again.</p>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function getPillarIcon(pillar: string): string {
  const icons: Record<string, string> = {
    'Faith': 'heart',
    'Family': 'home-heart',
    'Friends': 'group',
    'Fitness': 'run',
    'Finances': 'money-dollar-circle',
    'Food': 'restaurant',
    'Fun': 'emotion-happy'
  };
  return icons[pillar] || 'star';
}